import Joi from "joi-browser";

enum AnalysisType {
  Sentiment = "sentiment",
  Intent = "intent",
  param_extraction = "param_extraction",
}

enum TextSource {
  Last_Message = "last_message",
  Full_Conversation = "full_conversation",
  Attribute = "attribute",
}

interface BaseExtractionParameter {
  parameterName: string;
  parameterDescription: string;
}

type BooleanTypeExtractionParameter = BaseExtractionParameter & {
  parameterType: "boolean";
};

type OpenTypeExtractionParameter = BaseExtractionParameter & {
  parameterType: "open";
};

type ChoiceTypeExtractionParameter = BaseExtractionParameter & {
  parameterType: "choice";
  parameterChoices: [];
};

type ExtractionParameter =
  | BooleanTypeExtractionParameter
  | OpenTypeExtractionParameter
  | ChoiceTypeExtractionParameter;

type AnalysisNode = {
  name: string;
  nodeType: "analysis";
  description?: string;
  analysisType: AnalysisType;
  textSource: TextSource;
  textVariable?: string;
  outputVariableName: string;
  analysisPrompt?: string;
  parameter?: ExtractionParameter;
  output?: string;
};

const defaultAnalysisNode = {
  nodeType: "analysis",
  settings: {
    description: "",
    name: "AnalysisNode",
    analysisType: "",
    textSource: "",
    attributeToCheck: "",
    outputVariableName: "",
    analysisPrompt: "",
    parameter: "",
    output: "",
  },
};

const defaultExtractionParameter = {
  parameterName: "",
  parameterDescription: "",
  parameterType: "choice",
  parameterChoices: [],
};

const analysisNodeTemplate = [
  {
    name: "name",
    label: "Name",
    type: "text",
  },
  {
    name: "description",
    label: "Description",
    type: "textArea",
    style: { height: "50px" },
  },
  {
    name: "analysisType",
    label: "Analysis Type",
    type: "select",
    options: [
      { id: 0, value: "", label: "(None Selected)" },
      // { id: 1, value: AnalysisType.Intent, label: AnalysisType.Intent },
      // { id: 2, value: AnalysisType.Sentiment, label: AnalysisType.Sentiment },
      {
        id: 3,
        value: AnalysisType.param_extraction,
        label: "Look for a specific keyword",
      },
    ],
  },
  {
    name: "textSource",
    label: "Conversation Source",
    type: "select",
    options: [
      { id: 0, value: "", label: "(None selected)" },
      {
        id: 1,
        value: TextSource.Full_Conversation,
        label: "Full Conversation",
      },
      { id: 2, value: TextSource.Last_Message, label: "Last Received Message" },
      // { id: 3, value: TextSource.Attribute, label: TextSource.Attribute },
    ],
  },
  // {
  //   name: "attributeToCheck",
  //   label: "Attribute Name",
  //   type: "text",
  // },
  {
    name: "outputVariableName",
    label: "Keyword Name",
    type: "text",
  },
  // {
  //   name: "analysisPrompt",
  //   label: "Analysis Prompt",
  //   type: "textArea",
  //   style: { height: "50px" },
  // },
  // {
  //   name: "output",
  //   label: "Output",
  //   type: "text",
  // },
];

const analysisNodeSchema = {
  name: Joi.string().required().label("Name"),
  description: Joi.string().allow(null, ""),
  analysisType: Joi.string().required().label("Analysis Type"),
  textSource: Joi.string().required().label("Conversation Source"),
  attributeToCheck: Joi.string().allow(null, ""),
  outputVariableName: Joi.string().required().label("Keyword Name"),
  analysisPrompt: Joi.string().allow(null, ""),
  output: Joi.string().allow(null, ""),
};

type KeywordAnalysisNode = {
  nodeType: "analysis";
  settings: {
    name: string;
    description?: string;
    analysisType: AnalysisType.param_extraction;
    textSource: TextSource.Full_Conversation;
    outputVariableName: string;
    parameter: ExtractionParameter;
  };
};

const keywordAnalysisNodeTemplate = [
  {
    name: "name",
    label: "Name",
    type: "text",
  },
  {
    name: "description",
    label: "Description",
    type: "textArea",
    style: { height: "50px", fontSize: "13px" },
  },
  {
    name: "outputVariableName",
    label: "Keyword Name *",
    type: "text",
  },
  {
    name: "parameterDescription",
    label: "Keyword Prompt *",
    description: "Describe What the AI should extract",
    type: "textArea",
    style: { height: "100px", fontSize: "13px" },
  },
  {
    name: "parameterChoices",
    label: "Keyword Values *",
    description: "Add the list of values expected for the Keyword",
    type: "inputFieldList",
  },
];

const parameterSchema = {
  parameterName: Joi.string().required().max(50).label("Name"),
  parameterDescription: Joi.string().required().label("Keyword Prompt"),
  parameterType: Joi.string().required().max(300).label("Type of field"),
  parameterChoices: Joi.array()
    .items(Joi.string().label("Option"))
    .min(1)
    .label("Keyword Values"),
};

const keywordAnalysisNodeSchema = {
  name: Joi.string().required().label("Name"),
  description: Joi.string().allow(null, ""),
  analysisType: Joi.string().required().label("Analysis Type"),
  textSource: Joi.string().required().label("Conversation Source"),
  outputVariableName: Joi.string().required().label("Keyword Name"),
  parameter: Joi.object(parameterSchema),
};

const keywordAnalysisNodeSchemaWithParameterDescriptionAndChoice = {
  name: keywordAnalysisNodeSchema.name,
  description: keywordAnalysisNodeSchema.description,
  analysisType: keywordAnalysisNodeSchema.analysisType,
  textSource: keywordAnalysisNodeSchema.textSource,
  outputVariableName: keywordAnalysisNodeSchema.outputVariableName,
  parameterDescription: parameterSchema.parameterDescription,
  parameterChoices: parameterSchema.parameterChoices,
};

const defaultKeywordAnalysisNode = {
  nodeType: "analysis",
  settings: {
    name: "Keyword Analysis",
    description: "",
    analysisType: AnalysisType.param_extraction,
    textSource: TextSource.Full_Conversation,
    outputVariableName: "",
    parameter: {
      parameterName: "",
      parameterDescription: "",
      parameterType: "choice",
      parameterChoices: [""],
    },
  },
};

type AnalysisNodes = KeywordAnalysisNode;

export {
  AnalysisType,
  TextSource,
  AnalysisNode,
  ExtractionParameter,
  defaultAnalysisNode,
  defaultExtractionParameter,
  analysisNodeTemplate,
  analysisNodeSchema,
  KeywordAnalysisNode,
  defaultKeywordAnalysisNode,
  keywordAnalysisNodeSchema,
  keywordAnalysisNodeSchemaWithParameterDescriptionAndChoice,
  keywordAnalysisNodeTemplate,
  AnalysisNodes,
};
